import React from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

/**
 * Markdown that understands links into the platform.
 *
 * A link written as `[Inner Exploration](/dashboard/forum?category=…)` navigates inside the app —
 * no page reload, no chance of dropping the session. Anything external opens in a new tab.
 */
export function MarkdownWithLinks({ children, className = '', inline = false }) {
  const navigate = useNavigate();

  const Anchor = ({ href = '', children: label }) => {
    const internal = href.startsWith('/dashboard') || href.startsWith('/booking')
      || href.startsWith('/blog') || href === '/';
    if (internal) {
      return (
        <a
          href={href}
          onClick={(e) => { e.preventDefault(); navigate(href); }}
          className="text-cyan-700 underline decoration-cyan-300 hover:decoration-cyan-600"
          data-testid="internal-link"
        >
          {label}
        </a>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
        className="text-cyan-700 underline decoration-cyan-300 hover:decoration-cyan-600">
        {label}
      </a>
    );
  };

  // `inline` keeps the surrounding typography: no prose wrapper, no paragraph tags. Used where the
  // text already sits inside a heading or a <label>, which cannot legally contain a <p>.
  if (inline) {
    return (
      <span className={className}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkBreaks]}
          components={{ a: Anchor, p: ({ children: kids }) => <>{kids}</> }}
        >
          {children || ''}
        </ReactMarkdown>
      </span>
    );
  }

  return (
    <div className={`prose max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{ a: Anchor }}
      >
        {children || ''}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownWithLinks;
